import { useState, useEffect, useMemo } from 'react'
import { useExchange } from '../hooks/useExchange'
import { listTransferPlans, createTransferPlan, resolveTransferPlan, updateTransferPlan, getBranches } from '../api/services'
import { ApiError } from '../api/client'
import type { TransferPlan, TransferCurrencyCode, Branch } from '../api/types'
import FlagIcon from './FlagIcon'
import ConfirmModal from './ConfirmModal'
import LoadingModal from './LoadingModal'

const currencyMap: Record<string, string> = {
  KRW: 'kr', USD: 'us', JPY: 'jp', EUR: 'eu', CNY: 'cn',
  HKD: 'hk', TWD: 'tw', SGD: 'sg', THB: 'th', VND: 'vn',
  PHP: 'ph', MYR: 'my', AUD: 'au', CAD: 'ca', GBP: 'gb', IDR: 'id',
}

const currencyOptions: { code: TransferCurrencyCode; name: string }[] = [
  { code: 'KRW', name: '대한민국 원' },
  { code: 'USD', name: '미국 달러' },
  { code: 'JPY', name: '일본 엔' },
  { code: 'CNY', name: '중국 위안' },
  { code: 'EUR', name: '유로' },
  { code: 'HKD', name: '홍콩 달러' },
  { code: 'TWD', name: '대만 달러' },
  { code: 'SGD', name: '싱가포르 달러' },
  { code: 'THB', name: '태국 바트' },
  { code: 'VND', name: '베트남 동' },
  { code: 'PHP', name: '필리핀 페소' },
  { code: 'MYR', name: '말레이시아 링깃' },
  { code: 'AUD', name: '호주 달러' },
  { code: 'CAD', name: '캐나다 달러' },
  { code: 'GBP', name: '영국 파운드' },
  { code: 'IDR', name: '인도네시아 루피아' },
]

export default function TransferTab() {
  const { selectedBranch } = useExchange()

  // All branches (fetched from API, not just user's branches)
  const [allBranches, setAllBranches] = useState<Branch[]>([])

  // List state
  const [plans, setPlans] = useState<TransferPlan[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createCurrency, setCreateCurrency] = useState<TransferCurrencyCode>('KRW')
  const [createAmount, setCreateAmount] = useState('')
  const [createDirection, setCreateDirection] = useState<'send' | 'receive'>('send')
  const [createMemo, setCreateMemo] = useState('')
  const [createExternalName, setCreateExternalName] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  // Resolve state
  const [resolveTarget, setResolveTarget] = useState<TransferPlan | null>(null)
  const [resolveAmount, setResolveAmount] = useState('')
  const [isResolving, setIsResolving] = useState(false)

  // Memo edit state
  const [memoEditTarget, setMemoEditTarget] = useState<TransferPlan | null>(null)
  const [memoEditValue, setMemoEditValue] = useState('')
  const [isSavingMemo, setIsSavingMemo] = useState(false)

  // Result modal
  const [resultModal, setResultModal] = useState<{ title: string; message: string } | null>(null)

  const otherBranches = useMemo(() => {
    return allBranches.filter(b => b.nid !== selectedBranch?.nid)
  }, [allBranches, selectedBranch])

  const [createTargetBranch, setCreateTargetBranch] = useState<string>('')

  // "기타(없음) - 외부"가 기본값 (createTargetBranch === '')

  // Fetch all branches from API (includes other branches like 광장, 충무로)
  useEffect(() => {
    async function fetchAllBranches() {
      try {
        const response = await getBranches()
        setAllBranches(response.branches)
      } catch (err) {
        console.error('Failed to fetch all branches:', err)
      }
    }
    fetchAllBranches()
  }, [])

  const fetchPlans = async () => {
    if (!selectedBranch) return

    setLoading(true)
    setError(null)
    try {
      const response = await listTransferPlans({
        branchNid: selectedBranch.nid,
        page,
      })
      setPlans(response.plans)
      setTotal(response.total)
    } catch (err) {
      console.error('Failed to fetch transfer plans:', err)
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('거래 목록을 불러오는데 실패했습니다.')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPlans()
  }, [selectedBranch, page])

  const handleCreate = async () => {
    if (!selectedBranch) return

    const amount = parseInt(createAmount, 10)
    if (!amount || amount <= 0) return

    setIsCreating(true)
    try {
      await createTransferPlan({
        ...(createDirection === 'send'
          ? { sendingBranchNid: selectedBranch.nid, receivingBranchNid: createTargetBranch || undefined }
          : { receivingBranchNid: selectedBranch.nid, sendingBranchNid: createTargetBranch || undefined }
        ),
        currencyCode: createCurrency,
        amount,
        memo: createExternalName
          ? `[외부: ${createExternalName}]${createMemo ? ' ' + createMemo : ''}`
          : createMemo || undefined,
      })

      setResultModal({ title: '거래 생성 완료', message: '거래가 성공적으로 생성되었습니다.' })
      setShowCreateForm(false)
      setCreateAmount('')
      setCreateMemo('')
      setCreateExternalName('')
      fetchPlans()
    } catch (err) {
      console.error('Failed to create transfer plan:', err)
      if (err instanceof ApiError) {
        setResultModal({ title: '거래 생성 실패', message: err.message })
      } else {
        setResultModal({ title: '거래 생성 실패', message: '거래 생성 중 오류가 발생했습니다.' })
      }
    } finally {
      setIsCreating(false)
    }
  }

  const handleResolve = async () => {
    if (!selectedBranch || !resolveTarget) return

    const amount = parseFloat(resolveAmount)
    if (!amount || amount <= 0) return

    setIsResolving(true)
    try {
      await resolveTransferPlan({
        branchNid: selectedBranch.nid,
        planNid: resolveTarget.nid,
        amount,
      })

      setResultModal({ title: '정산 완료', message: '거래가 정산되었습니다.' })
      setResolveTarget(null)
      setResolveAmount('')
      fetchPlans()
    } catch (err) {
      console.error('Failed to resolve transfer plan:', err)
      if (err instanceof ApiError) {
        setResultModal({ title: '정산 실패', message: err.message })
      } else {
        setResultModal({ title: '정산 실패', message: '정산 처리 중 오류가 발생했습니다.' })
      }
    } finally {
      setIsResolving(false)
    }
  }

  const handleSaveMemo = async () => {
    if (!selectedBranch || !memoEditTarget) return

    setIsSavingMemo(true)
    try {
      await updateTransferPlan({
        branchNid: selectedBranch.nid,
        planNid: memoEditTarget.nid,
        memo: memoEditValue || undefined,
      })

      setMemoEditTarget(null)
      setMemoEditValue('')
      fetchPlans()
    } catch (err) {
      console.error('Failed to update memo:', err)
      if (err instanceof ApiError) {
        setResultModal({ title: '메모 저장 실패', message: err.message })
      } else {
        setResultModal({ title: '메모 저장 실패', message: '메모 저장 중 오류가 발생했습니다.' })
      }
    } finally {
      setIsSavingMemo(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatNumber = (num: number | string) => {
    return Number(num).toLocaleString('ko-KR')
  }

  const getCountryCode = (currencyCode: string) => {
    return currencyMap[currencyCode] || 'unknown'
  }

  const getPlanSummary = (plan: TransferPlan) => {
    if (plan.type === 'internal') {
      return {
        label: '지점간 이동',
        badge: 'bg-blue-100 text-blue-800',
        detail: `${plan.sendingBranch.name} → ${plan.receivingBranch.name}`,
        currency: plan.currencyCode,
        amount: plan.amount,
      }
    }
    if (plan.type === 'external') {
      const externalMatch = plan.memo?.match(/^\[외부: (.+?)\]/)
      const externalName = externalMatch ? `기타 (${externalMatch[1]})` : plan.branch.name
      return {
        label: plan.direction === 'sending' ? '보낼 돈' : '받을 돈',
        badge: plan.direction === 'sending' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800',
        detail: `${plan.direction === 'sending' ? '→' : '←'} ${externalName}`,
        currency: plan.currencyCode,
        amount: plan.amount,
      }
    }
    // unknown type
    const firstItem = plan.items[0]
    return {
      label: '복합 거래',
      badge: 'bg-gray-100 text-gray-800',
      detail: `${plan.items.length}건`,
      currency: firstItem?.currencyCode || 'KRW',
      amount: firstItem?.amount || 0,
    }
  }

  const getResolveStatus = (plan: TransferPlan) => {
    const expectedAmount = plan.type === 'unknown' ? 0 :
      plan.type === 'internal' ? plan.amount :
      plan.amount

    if (plan.resolvedAmount === 0) return { label: '미정산', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' }
    if (plan.resolvedAmount >= expectedAmount) return { label: '정산완료', color: 'text-green-600 bg-green-50 border-green-200' }
    return { label: '부분정산', color: 'text-orange-600 bg-orange-50 border-orange-200' }
  }

  if (loading && plans.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-fx-text-dark">자금이동</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchPlans}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            새로고침
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 text-sm bg-fx-confirm text-white rounded-lg hover:opacity-90 transition-all font-medium"
          >
            + 새 거래
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchPlans} className="text-sm text-red-600 hover:text-red-800 underline">
            다시 시도
          </button>
        </div>
      )}

      {/* Plans Table */}
      <div className="flex-1 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="h-full overflow-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">일시</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">유형</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">통화 / 금액</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">상세</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">정산</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">메모</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {plans.map((plan) => {
                const summary = getPlanSummary(plan)
                const resolveStatus = getResolveStatus(plan)

                return (
                  <tr key={plan.nid} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {formatDate(plan.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${summary.badge}`}>
                        {summary.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <FlagIcon countryCode={getCountryCode(summary.currency)} size="sm" className="rounded-full shadow-sm" />
                        <span className="font-medium">{formatNumber(summary.amount)}</span>
                        <span className="text-gray-500">{summary.currency}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                      {plan.type === 'unknown' ? (
                        <div className="space-y-1">
                          {plan.items.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-1 text-xs">
                              <span className={item.direction === 'sending' ? 'text-red-500' : 'text-green-500'}>
                                {item.direction === 'sending' ? '↑ 보낼 돈' : '↓ 받을 돈'}
                              </span>
                              <span>{formatNumber(item.amount)} {item.currencyCode}</span>
                              <span className="text-gray-400">({item.branch.name})</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        summary.detail
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex items-center px-2 py-1 rounded border text-xs font-medium ${resolveStatus.color}`}>
                        {resolveStatus.label}
                        {plan.resolvedAmount > 0 && (
                          <span className="ml-1">({formatNumber(plan.resolvedAmount)} {getPlanSummary(plan).currency})</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate">
                      {(plan.memo?.replace(/^\[외부: .+?\]\s*/, '') || '') || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setResolveTarget(plan)
                            setResolveAmount('')
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 font-medium px-2 py-1 rounded transition-colors text-xs"
                        >
                          정산
                        </button>
                        <button
                          onClick={() => {
                            setMemoEditTarget(plan)
                            setMemoEditValue(plan.memo || '')
                          }}
                          className="text-gray-500 hover:text-gray-700 hover:bg-gray-50 font-medium px-2 py-1 rounded transition-colors text-xs"
                        >
                          메모
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              {plans.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    거래 내역이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            이전
          </button>
          <span className="text-sm text-gray-600">
            {page} 페이지 (총 {total}건)
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={plans.length < 20}
            className="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            다음
          </button>
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => !isCreating && setShowCreateForm(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">새 거래 등록</h2>

            <div className="space-y-4">
              {/* Direction */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">방향</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setCreateDirection('send')}
                    className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all ${
                      createDirection === 'send'
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    줄 돈
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreateDirection('receive')}
                    className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all ${
                      createDirection === 'receive'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    받을 돈
                  </button>
                </div>
              </div>

              {/* Target Branch */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {createDirection === 'send' ? '수신 지점' : '송신 지점'}
                </label>
                <select
                  value={createTargetBranch}
                  onChange={(e) => setCreateTargetBranch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">기타(없음) - 외부</option>
                  {otherBranches.map(b => (
                    <option key={b.nid} value={b.nid}>{b.name}</option>
                  ))}
                </select>
                {!createTargetBranch && (
                  <input
                    type="text"
                    value={createExternalName}
                    onChange={(e) => setCreateExternalName(e.target.value)}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="거래처 이름 (선택)"
                  />
                )}
              </div>

              {/* Currency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">통화</label>
                <select
                  value={createCurrency}
                  onChange={(e) => setCreateCurrency(e.target.value as TransferCurrencyCode)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {currencyOptions.map(c => (
                    <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">금액</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={createAmount ? formatNumber(parseInt(createAmount, 10)) : ''}
                  onChange={(e) => setCreateAmount(e.target.value.replace(/[^0-9]/g, ''))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  placeholder="0"
                />
              </div>

              {/* Memo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">메모 (선택)</label>
                <input
                  type="text"
                  value={createMemo}
                  onChange={(e) => setCreateMemo(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="메모를 입력하세요"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateForm(false)}
                disabled={isCreating}
                className="flex-1 bg-fx-reset text-white px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-all"
              >
                취소
              </button>
              <button
                onClick={handleCreate}
                disabled={isCreating || !createAmount || parseInt(createAmount, 10) <= 0}
                className="flex-1 bg-fx-confirm text-white px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? '등록 중...' : '등록'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {resolveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => !isResolving && setResolveTarget(null)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">정산 처리</h2>

            <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-2">
              {(() => {
                const summary = getPlanSummary(resolveTarget)
                return (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">유형</span>
                      <span className="font-medium">{summary.label}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">통화 / 금액</span>
                      <span className="font-medium">{formatNumber(summary.amount)} {summary.currency}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">상세</span>
                      <span className="font-medium">{summary.detail}</span>
                    </div>
                    {resolveTarget.resolvedAmount > 0 && (
                      <div className="flex justify-between text-sm border-t pt-2">
                        <span className="text-gray-500">기정산액</span>
                        <span className="font-medium text-green-600">{formatNumber(resolveTarget.resolvedAmount)}</span>
                      </div>
                    )}
                  </>
                )
              })()}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">정산 금액</label>
              <input
                type="text"
                inputMode="numeric"
                value={resolveAmount ? formatNumber(parseFloat(resolveAmount)) : ''}
                onChange={(e) => setResolveAmount(e.target.value.replace(/[^0-9]/g, ''))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                placeholder="0"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setResolveTarget(null)}
                disabled={isResolving}
                className="flex-1 bg-fx-reset text-white px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-all"
              >
                취소
              </button>
              <button
                onClick={handleResolve}
                disabled={isResolving || !resolveAmount || parseFloat(resolveAmount) <= 0}
                className="flex-1 bg-fx-confirm text-white px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResolving ? '처리 중...' : '정산 확인'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Memo Edit Modal */}
      {memoEditTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => !isSavingMemo && setMemoEditTarget(null)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">메모 수정</h2>

            <div className="mb-4">
              <textarea
                value={memoEditValue}
                onChange={(e) => setMemoEditValue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="메모를 입력하세요"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setMemoEditTarget(null)}
                disabled={isSavingMemo}
                className="flex-1 bg-fx-reset text-white px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-all"
              >
                취소
              </button>
              <button
                onClick={handleSaveMemo}
                disabled={isSavingMemo}
                className="flex-1 bg-fx-confirm text-white px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingMemo ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Modal */}
      <ConfirmModal
        isOpen={!!resultModal}
        title={resultModal?.title || ''}
        message={resultModal?.message}
        confirmText="확인"
        cancelText=""
        onConfirm={() => setResultModal(null)}
        onCancel={() => setResultModal(null)}
      />

      {/* Loading Modal */}
      <LoadingModal isOpen={isCreating || isResolving || isSavingMemo} message="처리 중..." />
    </div>
  )
}
